import { AudioLines, Film, Image as ImageIcon } from "lucide-react";

import type { Job } from "../../api/generated/model";
import { expiredLine, isExpired } from "../../platform/jobs/expired";
import { isSettled } from "../../platform/jobs/status";
import { TYPE_NAMES } from "../../platform/jobs/type-names";
import { ThumbImage } from "../../ui/ThumbImage";
import { RunningMedia } from "./RunningMedia";
import classes from "./create/create.module.css";
import home from "./pro-home.module.css";

/** A settled job's badge when it made nothing. */
const ENDED: Partial<Record<Job["status"], string>> = {
  failed: "Failed",
  expired: "Expired",
  canceled: "Canceled",
};

interface ResultCardProps {
  job: Job;
  /** The model's name, from the catalog. */
  model: string | null;
  /** A picture that would not load: the list is read again for fresh URLs. */
  onLost: () => void;
  /** Opens the job's detail, the Studio's own (decision 27). */
  onOpen: () => void;
}

/**
 * The prototype's result card (`genCard`): the result's picture with its state, then its type and
 * model; the picture opens its detail. A video shows its still, never its file; an image its
 * thumbnail first (issue 828, item 12); a sound, which has no picture, the waveform.
 */
export function ResultCard({ job, model, onLost, onOpen }: ResultCardProps) {
  const output = job.outputs.find((each) => each.url !== null);
  const video = job.type.startsWith("video.");
  // Only an image's own file is a picture: a sound's is an audio file, never an <img>.
  const image = job.type.startsWith("image.");
  const NoPicture = video ? Film : image ? ImageIcon : AudioLines;
  const name = TYPE_NAMES[job.type].name;
  return (
    <article className={classes.result} aria-label={name}>
      <button
        type="button"
        className={classes.resultMedia}
        aria-label={`Open this ${name.toLowerCase()}`}
        onClick={onOpen}
      >
        {video && output?.poster_url != null ? (
          <ThumbImage thumb={null} full={output.poster_url} alt="" onLost={onLost} />
        ) : image && output?.url != null ? (
          <ThumbImage thumb={output.thumb_url} full={output.url} alt="" onLost={onLost} />
        ) : !isSettled(job.status) ? (
          // The current Studio's running tile, not a flat icon (spec ui-switch, e9).
          <RunningMedia job={job} />
        ) : (
          <NoPicture size={24} strokeWidth={1.5} className={home.mediaIcon} aria-hidden />
        )}
        {isSettled(job.status) ? (
          job.status === "succeeded" ? (
            // Its file gone, never saved: the line in a plain badge, not an error's.
            output === undefined && job.outputs.some(isExpired) ? (
              <span className={home.badge}>{expiredLine(job)}</span>
            ) : null
          ) : (
            <span className={home.badge} data-tone="err">
              {ENDED[job.status]}
            </span>
          )
        ) : (
          <span className={home.badge} data-tone="v">
            Running
          </span>
        )}
      </button>
      <span className={home.facts}>
        <span className={home.badge}>{name}</span>
        {model === null ? null : <span className={home.model}>{model}</span>}
      </span>
    </article>
  );
}
